import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { X, Printer, DownloadSimple } from '@phosphor-icons/react';
import { typeMeta } from '@/components/AccountCard';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

/** Return [startDate, endDate] (JS Date objects, inclusive / exclusive resp.) for a preset. */
function rangeForPreset(preset, customFrom, customTo) {
  if (preset === 'all') return [null, null];
  const now = new Date();
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const endOfDay   = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
  if (preset === 'today')  return [startOfDay(now), endOfDay(now)];
  if (preset === 'week') {
    const start = startOfDay(now);
    start.setDate(start.getDate() - start.getDay()); // Sunday as week start
    return [start, endOfDay(now)];
  }
  if (preset === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return [start, endOfDay(now)];
  }
  if (preset === 'custom') {
    const f = customFrom ? startOfDay(new Date(customFrom)) : null;
    const t = customTo   ? endOfDay(new Date(customTo))     : null;
    return [f, t];
  }
  return [null, null];
}

/**
 * Full-page ledger for a single balance-sheet head (account).
 *
 * Columns: Date · Bill No · Description · Debit · Credit · Balance.
 * The running balance is computed chronologically (oldest → newest) so the
 * very last row matches the current account balance. On screen we render
 * newest-first, but each row carries the running balance that existed at
 * that transaction's moment in time.
 *
 * For asset-side accounts (cash/bank/wallet/debtor/asset): income is a debit
 * entry (money in), expense is a credit entry (money out). For liability-side
 * accounts (creditor/capital) we flip the convention so debit means a payout
 * (money out of our pocket) and credit means money received/owed.
 */
export default function AccountLedgerDialog({ account, open, onOpenChange }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    if (!open || !account) return;
    setLoading(true);
    setPreset('all'); setCustomFrom(''); setCustomTo('');
    api.get(`/finance?account=${account.id}`)
      .then(r => setTxns(r.data || []))
      .catch(() => setTxns([]))
      .finally(() => setLoading(false));
  }, [open, account]);

  const meta = account ? typeMeta(account.type) : null;
  const isLiability = meta?.side === 'liability';

  const { rows, openingBalance, totals, rangeLabel } = useMemo(() => {
    // Sort oldest → newest so we walk the running balance forwards.
    const sorted = [...txns].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    const [from, to] = rangeForPreset(preset, customFrom, customTo);

    // Step 1: Classify each txn into debit/credit for the current account side.
    // We DROP every leg that originated from applying a stored advance (the
    // `advance_used` row, plus any income / govt_fee_collected legs stamped
    // `from_advance: true`). None of those moved cash — the cash had already
    // landed when the advance was first received — so showing them in the
    // cash/bank ledger is misleading and double-counts the receipt.
    const filtered = sorted.filter(t => t.type !== 'advance_used' && !t.from_advance);

    // For asset-side (cash/bank/wallet/debtor/asset): "debit" = money in, "credit" = money out.
    // For liability-side (creditor/capital): flipped — "debit" = money paid out of our pocket,
    //                                                "credit" = money received / amount owed.
    //
    // Income-like types (money in for asset accounts):
    //   income, govt_fee_collected, transfer_in, opening_balance,
    //   advance_received, adjustment(credit)
    // Expense-like types (money out for asset accounts):
    //   expense, govt_fee, transfer_out, adjustment(debit)
    const classify = (t) => {
      const amt = Number(t.amount) || 0;
      let inflow = 0, outflow = 0;
      const type = t.type;
      if (type === 'income' || type === 'govt_fee_collected' || type === 'transfer_in' ||
          type === 'opening_balance' || type === 'advance_received') {
        inflow = amt;
      } else if (type === 'expense' || type === 'govt_fee' || type === 'transfer_out') {
        outflow = amt;
      } else if (type === 'adjustment') {
        if (t.direction === 'credit') inflow = amt; else outflow = amt;
      }
      // For liability accounts, flip the visual debit/credit columns but keep running-balance
      // math consistent with how the backend persists account.balance (inflow adds, outflow subtracts).
      if (isLiability) return { debit: outflow, credit: inflow, delta: inflow - outflow };
      return { debit: inflow, credit: outflow, delta: inflow - outflow };
    };

    // Step 2: Merge `income` + `govt_fee_collected` rows belonging to the same invoice
    // (same reference_id) on the same account into a single pseudo-row showing the total
    // amount received. Every other txn — including per-item govt_fee deductions — stays
    // as its own line so bank reconciliation is exact.
    const mergedMap = new Map(); // reference_id -> merged row
    const orderedKeys = [];      // preserves insertion order
    for (const t of filtered) {
      const canMerge = (t.type === 'income' || t.type === 'govt_fee_collected') && t.reference_id;
      if (!canMerge) {
        const key = `single:${t.id}`;
        mergedMap.set(key, { ...t, _merged: false });
        orderedKeys.push(key);
        continue;
      }
      const key = `inv:${t.reference_id}`;
      const existing = mergedMap.get(key);
      // Pull the bill number out of the description: "Service charge for INV-00001 - …"
      const invMatch = (t.description || '').match(/for\s+(INV-\S+|T-\S+|DRAFT-\S+)/i);
      const invNo = t.invoice_number || (invMatch ? invMatch[1] : '');
      if (!existing) {
        mergedMap.set(key, {
          id: key,
          type: 'invoice_received',
          amount: Number(t.amount) || 0,
          account: t.account,
          reference_id: t.reference_id,
          reference_number: t.reference_number || invNo,
          invoice_number: invNo,
          description: (t.description || '').replace(/^(Service charge|Govt fee collected) for /, 'Received for '),
          created_at: t.created_at,
          category: 'Invoice Received',
          _merged: true,
        });
        orderedKeys.push(key);
      } else {
        existing.amount = (Number(existing.amount) || 0) + (Number(t.amount) || 0);
        if (!existing.invoice_number && invNo) existing.invoice_number = invNo;
        if (!existing.reference_number && invNo) existing.reference_number = invNo;
        // Keep earliest created_at so row placement is stable.
        if ((t.created_at || '') < (existing.created_at || '')) existing.created_at = t.created_at;
      }
    }

    // Step 3: Walk merged list chronologically and compute running balance.
    let opening = 0;
    let running = 0;
    const walked = [];
    for (const key of orderedKeys) {
      const t = mergedMap.get(key);
      const { debit, credit, delta } = t._merged
        ? (isLiability
            ? { debit: 0, credit: t.amount, delta: t.amount }
            : { debit: t.amount, credit: 0, delta: t.amount })
        : classify(t);
      running += delta;
      const when = t.created_at ? new Date(t.created_at) : null;
      const beforeWindow = from && when && when < from;
      const afterWindow  = to   && when && when > to;
      if (beforeWindow) {
        opening = running;
        continue;
      }
      if (afterWindow) continue;
      walked.push({ ...t, debit, credit, balance: running });
    }
    const d = walked.reduce((s, r) => s + (r.debit || 0), 0);
    const c = walked.reduce((s, r) => s + (r.credit || 0), 0);

    let label;
    if (preset === 'all') label = 'All time';
    else if (preset === 'today') label = 'Today';
    else if (preset === 'week')  label = 'This week';
    else if (preset === 'month') label = 'This month';
    else if (preset === 'custom') {
      label = `${customFrom || '…'} → ${customTo || '…'}`;
    }

    return { rows: walked.reverse(), openingBalance: opening, totals: { debit: d, credit: c }, rangeLabel: label };
  }, [txns, isLiability, preset, customFrom, customTo]);

  if (!account) return null;

  const handlePrint = () => window.print();
  const handleExportCSV = () => {
    const header = ['Date', 'Bill No', 'Description', 'Debit', 'Credit', 'Balance'];
    const body = rows.slice().reverse().map(r => [
      formatDate(r.created_at),
      r.reference_number || r.invoice_number || '',
      (r.description || '').replace(/"/g, '""'),
      r.debit || '',
      r.credit || '',
      r.balance,
    ]);
    if (preset !== 'all') {
      body.unshift(['', '', 'Opening Balance', '', '', openingBalance]);
    }
    const csv = [header, ...body].map(row => row.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ledger-${account.name.replace(/\s+/g, '-')}-${preset}.csv`;
    a.click();
  };

  const PRESETS = [
    { v: 'all',    label: 'All' },
    { v: 'today',  label: 'Today' },
    { v: 'week',   label: 'Week' },
    { v: 'month',  label: 'Month' },
    { v: 'custom', label: 'Custom' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-none w-screen h-screen rounded-none border-0 p-0 overflow-hidden flex flex-col"
        data-testid="ledger-dialog"
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b border-[#E4E4E7] ${meta?.tint || ''}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${meta?.bg || 'bg-zinc-100'} flex items-center justify-center`}>
              {meta?.icon && <meta.icon size={20} className={meta?.text} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }}>
                  {account.name}
                </h2>
                <Badge className={`font-medium border uppercase tracking-wide text-[9px] ${meta?.text}`} variant="outline">
                  {meta?.label}
                </Badge>
              </div>
              <p className="text-xs text-[#52525B]">Ledger · all transactions in chronological order</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="rounded-lg" data-testid="ledger-export-csv">
              <DownloadSimple size={14} className="mr-1" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} className="rounded-lg" data-testid="ledger-print">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-9 w-9 p-0 rounded-full hover:bg-[#F4F4F5]"
              data-testid="ledger-close"
              aria-label="Close"
            >
              <X size={18} />
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 px-6 py-2.5 border-b border-[#E4E4E7] bg-white flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.12em] text-[#52525B] font-semibold">Range:</span>
          {PRESETS.map(p => (
            <button
              key={p.v}
              type="button"
              onClick={() => setPreset(p.v)}
              data-testid={`ledger-range-${p.v}`}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                preset === p.v
                  ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                  : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'
              }`}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <div className="flex items-center gap-1">
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-7 w-36 text-xs" data-testid="ledger-range-from" />
              <span className="text-xs text-[#52525B]">→</span>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-7 w-36 text-xs" data-testid="ledger-range-to" />
            </div>
          )}
          {rangeLabel && preset !== 'all' && (
            <span className="ml-auto text-[11px] text-[#52525B]">Showing: <b className="text-[#0A0A0A]">{rangeLabel}</b></span>
          )}
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-4 gap-0 border-b border-[#E4E4E7] bg-[#FAFAFA]">
          <SummaryCell label="Transactions" value={rows.length} mono={false} />
          <SummaryCell label={isLiability ? 'Debit (paid out)' : 'Debit (received)'} value={fmt(totals.debit)} color="text-emerald-600" />
          <SummaryCell label={isLiability ? 'Credit (received)' : 'Credit (paid out)'} value={fmt(totals.credit)} color="text-red-600" />
          <SummaryCell
            label="Closing Balance"
            value={fmt(account.balance || 0)}
            color={(account.balance || 0) < 0 ? 'text-red-600' : 'text-[#0A0A0A]'}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <p className="text-sm text-[#52525B]">Loading ledger…</p>
          ) : rows.length === 0 && preset === 'all' ? (
            <div className="py-20 text-center">
              <p className="text-sm text-[#52525B]">No transactions yet for {account.name}.</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-[#E4E4E7] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-40">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-32">Bill No</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-32">Debit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-32">Credit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-36">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-[#E4E4E7] bg-[#FFFBEB]">
                    <TableCell className="text-xs text-[#D97706] whitespace-nowrap font-semibold" colSpan={3}>
                      Opening Balance ({rangeLabel || 'selected range'})
                    </TableCell>
                    <TableCell className="text-sm text-right"><span className="text-[#A1A1AA]">—</span></TableCell>
                    <TableCell className="text-sm text-right"><span className="text-[#A1A1AA]">—</span></TableCell>
                    <TableCell className={`text-sm text-right font-mono font-bold ${openingBalance < 0 ? 'text-red-600' : 'text-[#D97706]'}`}>
                      {fmt(openingBalance)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-sm text-[#52525B]">
                      No transactions in {rangeLabel}.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-lg border border-[#E4E4E7] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-40">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-32">Bill No</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-32">Debit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-32">Credit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-36">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preset !== 'all' && (
                    <TableRow className="border-[#E4E4E7] bg-[#FFFBEB]" data-testid="ledger-opening-row">
                      <TableCell className="text-xs text-[#D97706] whitespace-nowrap font-semibold" colSpan={3}>
                        Opening Balance ({rangeLabel || 'selected range'})
                      </TableCell>
                      <TableCell className="text-sm text-right"><span className="text-[#A1A1AA]">—</span></TableCell>
                      <TableCell className="text-sm text-right"><span className="text-[#A1A1AA]">—</span></TableCell>
                      <TableCell className={`text-sm text-right font-mono font-bold ${openingBalance < 0 ? 'text-red-600' : 'text-[#D97706]'}`}>
                        {fmt(openingBalance)}
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r, idx) => {
                    // Extract bill number from description for non-merged rows when not already set.
                    let billNo = r.reference_number || r.invoice_number;
                    if (!billNo && r.description) {
                      const m = r.description.match(/for\s+(INV-\S+|T-\S+|DRAFT-\S+)/i);
                      if (m) billNo = m[1];
                    }
                    return (
                    <TableRow
                      key={r.id || idx}
                      className={`border-[#E4E4E7] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} hover:bg-[#F0F9FF]`}
                      data-testid={`ledger-row-${r.id || idx}`}
                    >
                      <TableCell className="text-xs text-[#52525B] whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                      <TableCell className="text-xs font-mono text-[#3B82F6]">
                        {billNo || <span className="text-[#A1A1AA]">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-[#0A0A0A]">{r.description || '—'}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-emerald-700">
                        {r.debit > 0 ? fmt(r.debit) : <span className="text-[#A1A1AA]">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono text-red-600">
                        {r.credit > 0 ? fmt(r.credit) : <span className="text-[#A1A1AA]">—</span>}
                      </TableCell>
                      <TableCell className={`text-sm text-right font-mono font-semibold ${r.balance < 0 ? 'text-red-600' : 'text-[#0A0A0A]'}`}>
                        {fmt(r.balance)}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value, color = 'text-[#0A0A0A]', mono = true }) {
  return (
    <div className="px-6 py-3 border-r border-[#E4E4E7] last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#52525B]">{label}</div>
      <div className={`text-lg font-bold ${mono ? 'font-mono' : ''} ${color}`}>{value}</div>
    </div>
  );
}
